const trainingCollective = (root, wording) => ({
  areas: JSON.parse(root.querySelector('[data-training-areas-data]').textContent),
  wording,
  areaIndex: 0,
  exerciseIndex: 0,
  autoplayNext: true,
  isPlaying: false,
  posterVisible: true,
  canScrollRailBack: false,
  canScrollRailForward: false,
  canScrollStripBack: false,
  canScrollStripForward: false,
  preloadAllowed: false,

  init() {
    this.$nextTick(() => {
      const video = this.$refs.video;
      video.muted = true;
      video.addEventListener('playing', () => { this.isPlaying = true; this.posterVisible = false; });
      video.addEventListener('pause', () => { this.isPlaying = false; });
      video.addEventListener('ended', () => {
        this.isPlaying = false;
        if (this.autoplayNext) this.openExercise(this.exerciseIndex + 1, true);
      });
      video.addEventListener('canplaythrough', () => this.preloadNextVideo());

      window.addEventListener('resize', () => { this.checkRailEdges(); this.checkStripEdges(); });
      document.addEventListener('keydown', (event) => this.handleKeydown(event));

      this.openExercise(0, false);
      this.checkRailEdges();
      this.checkStripEdges();
      this.watchBlockVisibility();
    });
  },

  watchBlockVisibility() {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      this.allowPreload();
    }, { rootMargin: '600px' });

    observer.observe(root);
  },

  allowPreload() {
    this.preloadAllowed = true;

    const video = this.$refs.video;
    if (video.src && video.readyState === 0) {
      video.preload = 'auto';
      video.load();
      return;
    }

    this.preloadNextVideo();
  },

  preloadVideo(exercise) {
    if (!this.preloadAllowed) return;
    if (!exercise || exercise.media_type !== 'video') return;

    const source = this.pickSource(exercise);
    if (!source) return;

    const preloader = this.$refs.preloader;
    if (preloader.src === source) return;

    preloader.src = source;
    preloader.load();
  },

  preloadNextVideo() {
    const exercises = this.currentArea.exercises;
    if (exercises.length < 2) return;
    this.preloadVideo(exercises[(this.exerciseIndex + 1) % exercises.length]);
  },

  preloadArea(index) {
    const area = this.areas[index];
    if (!area || index === this.areaIndex) return;
    this.preloadVideo(area.exercises[0]);
  },

  get currentArea() {
    return this.areas[this.areaIndex] || { name: '', exercises: [] };
  },

  get currentExercise() {
    return this.currentArea.exercises[this.exerciseIndex] || {};
  },

  get isExternalVideo() {
    return this.currentExercise.media_type === 'external_video';
  },

  pickSource(exercise) {
    if (!exercise) return '';
    if (window.innerWidth < 768) return exercise.source_mobile || exercise.source || '';
    return exercise.source || exercise.source_mobile || '';
  },

  get queueTitle() {
    return `${this.currentArea.name} · ${this.currentArea.exercises.length} ${this.wording.exercises}`;
  },

  get stageMeta() {
    const total = this.currentArea.exercises.length;
    const position = `${this.exerciseIndex + 1} of ${total}`;
    const length = this.formatDuration(this.currentExercise.duration);
    return length ? `${length}  ·  ${position}` : position;
  },

  formatDuration(milliseconds) {
    if (!milliseconds) return '';
    const totalSeconds = Math.round(milliseconds / 1000);
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
  },

  openArea(index) {
    this.areaIndex = index;
    this.keepAreaInView();
    this.openExercise(0, true);
    this.$nextTick(() => this.checkStripEdges());
  },

  openExercise(index, shouldPlay) {
    const exercises = this.currentArea.exercises;
    if (!exercises.length) return;
    this.exerciseIndex = (index + exercises.length) % exercises.length;
    this.loadCurrentVideo(shouldPlay && this.autoplayNext);
    this.$nextTick(() => {
      this.keepExerciseInView();
      this.checkStripEdges();
    });
  },

  loadCurrentVideo(shouldPlay) {
    const exercise = this.currentExercise;
    const video = this.$refs.video;
    const frame = this.$refs.frame;

    video.pause();
    this.isPlaying = false;
    this.posterVisible = true;

    if (exercise.media_type === 'external_video') {
      video.removeAttribute('src');
      frame.src = this.buildEmbedUrl(exercise, shouldPlay);
      this.preloadNextVideo();
      return;
    }

    frame.removeAttribute('src');
    video.muted = true;
    video.preload = this.preloadAllowed || shouldPlay ? 'auto' : 'none';
    video.poster = exercise.poster || '';
    video.src = this.pickSource(exercise);
    video.load();
    if (shouldPlay) video.play().catch(() => {});
  },

  buildEmbedUrl(exercise, shouldPlay) {
    const autoplay = shouldPlay ? 1 : 0;
    if (exercise.host === 'vimeo') {
      return `https://player.vimeo.com/video/${exercise.external_id}?muted=1&autoplay=${autoplay}`;
    }
    return `https://www.youtube.com/embed/${exercise.external_id}?rel=0&modestbranding=1&playsinline=1&mute=1&autoplay=${autoplay}`;
  },

  keepAreaInView() {
    const rail = this.$refs.rail;
    const card = rail.children[this.areaIndex];
    if (!card) return;
    const cardEnd = card.offsetLeft + card.clientWidth;
    if (card.offsetLeft < rail.scrollLeft) {
      rail.scrollTo({ left: Math.max(0, card.offsetLeft - 12), behavior: 'smooth' });
    } else if (cardEnd > rail.scrollLeft + rail.clientWidth) {
      rail.scrollTo({ left: cardEnd - rail.clientWidth + 12, behavior: 'smooth' });
    }
  },

  keepExerciseInView() {
    const strip = this.$refs.strip;
    const item = strip.querySelectorAll('.training-queue-item')[this.exerciseIndex];
    if (!item) return;
    const centered = item.offsetLeft - (strip.clientWidth - item.clientWidth) / 2;
    strip.scrollTo({ left: Math.max(0, centered), behavior: 'smooth' });
  },

  togglePlayback() {
    const video = this.$refs.video;
    video.paused ? video.play().catch(() => {}) : video.pause();
  },

  toggleFromStage(event) {
    if (event.target.closest('button') || this.isExternalVideo) return;
    this.togglePlayback();
  },

  scrollRail(distance) {
    this.$refs.rail.scrollBy({ left: distance, behavior: 'smooth' });
  },

  scrollStrip(distance) {
    this.$refs.strip.scrollBy({ left: distance, behavior: 'smooth' });
  },

  checkRailEdges() {
    const rail = this.$refs.rail;
    this.canScrollRailBack = rail.scrollLeft > 0;
    this.canScrollRailForward = rail.scrollLeft < rail.scrollWidth - rail.clientWidth - 1;
  },

  checkStripEdges() {
    const strip = this.$refs.strip;
    this.canScrollStripBack = strip.scrollLeft > 0;
    this.canScrollStripForward = strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 1;
  },

  handleKeydown(event) {
    if (!root.isConnected) return;
    const isInsideBlock = root.contains(document.activeElement) || root.matches(':hover');
    if (!isInsideBlock || event.target.matches('input, textarea, select')) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.openExercise(this.exerciseIndex + 1, true);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.openExercise(this.exerciseIndex - 1, true);
    }
    if (event.key === ' ' && !this.isExternalVideo) {
      event.preventDefault();
      this.togglePlayback();
    }
  }
});

window._sections = window._sections || {};
window._sections["trainingCollective"] = trainingCollective;
